/**
 * QA Validation Skill Content — universal check layer for every model.
 *
 * This is a global hygiene skill — pushed, not pulled. It runs on every
 * build and every edit by default. Output and tool skills defer their
 * universal checks to it; each skill keeps its own specific checks.
 *
 * It owns: the four universal check groups (structural integrity, financial
 * articulation, sanity, edit integrity), the three-tier run schedule
 * (in-flight, step-gate, full run), and the shared result format that
 * every check — universal or skill-specific — emits.
 *
 * Skill Type: Model Hygiene
 * Shared Skill: Global
 */

export const QA_VALIDATION_SKILL_CONTENT = `## Skill: QA Validation

### Purpose
Be the universal check layer that runs on every build and edit - structural integrity, financial articulation, sanity, and edit integrity - continuously while working and as a full battery before output, so an error is caught at the step it's introduced rather than discovered at the end.

The worst outcome in modelling is a wrong answer that looks right - a balance forced by a plug, a units error 1,000x off, an edit that quietly moved a cell it shouldn't have. QA's job is to turn "usually right" into "provably right" on everything that is deterministic, and to flag for a human everything that is merely implausible.

### What Good Looks Like
A validated model lets a reviewer trust, without re-checking by hand:
1. There are no errors, and the formulas are consistent - no #REF!, the same formula shape fills across each row, links intact.
2. The financials articulate and tie - the balance sheet balances, cash flow ties to balance-sheet cash, EV->equity ties, reconciliations hold.
3. The numbers are sane - nothing is an unexplained outlier; margins, multiples and rates sit in believable ranges.
4. Every edit moved only what it should - links preserved, nothing displaced or pasted over.
5. Every check reports the same way - hard fails block, warnings surface, each with the cells it points to.

### When Validation Runs - Three Tiers
Checking is continuous, but "continuous" does not mean re-running the whole model every keystroke - on a circular model that thrashes, and it's slow on any model. Three tiers, each scoped to what just changed:

**Tier 1 - In-flight discipline (while building or editing).** Behavioural rules the agent follows as it works, not a batch check:
- Edit only the target cells. Never edit a cell and then move it into position - edit in place.
- Preserve every link. Never paste-special-values over a formula to "fix" a number.
- Introduce no errors, and keep the input/formula/link classification intact.

**Tier 2 - Step-gate (after each meaningful block).** The local checks for what just changed: this schedule rolls; this statement ties; this edit touched only its dependents. Fast and scoped - not a full recompute.

**Tier 3 - Full run (before output).** The whole battery: all four universal groups plus the active skill's own specific checks. Nothing ships with an open hard fail.

**Trace technique (used at the gate and the full run).** Simulate a check through the model: take a representative input, change it, and confirm the change flows the whole chain - e.g. a revenue change -> EBITDA -> cash flow -> the debt sweep -> returns. A link that doesn't move reveals a hardcode mid-chain; a cell that moves when it shouldn't reveals a stray link.

### Universal Check Groups

**Group 1 - Structural integrity (hard fails):**
- No formula errors anywhere: #REF!, #DIV/0!, #VALUE!, #N/A, #NAME?.
- Formula consistency across periods: a forecast row is the same formula filled right (draggable), not a mix of formulas and typed numbers.
- No stray or unsourced hardcodes in calculated/forecast cells (rule: Data & Formula skills).
- Cross-sheet and external links intact - no broken references.
- Units and header consistency across blocks (rule: Data & Formula skills).

**Group 2 - Financial articulation (hard fails; balance/cash checks apply where a full integrated build exists):**
- Balance sheet balances every period: assets - (liabilities + equity) = 0 (full builds; rule: Three Statement Model skills) - and balances by construction, with no plug (check for a cell that references the imbalance, not just that the row reads 0).
- Cash tie-out: ending cash on the cash flow = cash on the balance sheet, every period.
- The statements articulate: net income -> retained earnings, net income -> cash flow, change in NWC -> cash flow, capex -> PP&E -> D&A.
- Schedules roll: PP&E and equity (beg + adds - reductions = end); the debt schedule roll is checked by Debt Schedule skills.
- EV->equity ties (rule: EV to Equity Bridge skills); per-share ties to diluted shares.
- Reconciliations: a rebuilt aggregate ties to its pulled value (rule: Data & Formula skills); segment sums tie to consolidated; Sources = Uses where present (rule: LBO skills).

**Group 3 - Sanity / reasonableness (warnings - flag for a human, don't block):**
- High/low bounds: each headline output within a plausible range.
- Outliers: a peer multiple, margin, growth rate or rate far outside its set or its history.
- Plausible rates: implied interest rate, WACC, tax rate, terminal growth within sane bands.
- Sign sanity: debt >= 0, shares >= basic, no negative where one is impossible.

**Group 4 - Edit integrity (hard fails on scope/links; runs after every edit):**
- Change-scope: only the intended dependents moved - diff the dependency set before and after; an unrelated line that moved signals a stray link or a hardcode.
- Link preservation: no formula was overwritten with a static value.
- No displacement: no cell was edited and then relocated; content sits where it was.
- No downstream breakage: the edit introduced no error and broke no roll or tie-out further down.

### Result Contract
Every check - universal or skill-specific - emits the same record:

check = { name, level: ok | warn | fail, detail, cells }
result = { status: pass | fail, checks: [ check, ... ] }

- status is fail if any check is fail. A hard fail at any tier blocks output; warnings surface but do not block.
- level meaning: fail = a deterministic violation that makes the output wrong (a balance break, a broken link, an out-of-scope edit). warn = a plausibility flag a human should judge (an outlier, an aggressive but intended assumption).
- Universal + specific both run. QA's four groups run on every model; the active skill's own specific checks run alongside; the two sets merge into one result. QA does not duplicate the skill-specific checks.

### Failure Modes
How the checker itself misfires - watch for these in QA's own behaviour.

| Anti-pattern | Why it's wrong | Instead |
|---|---|---|
| Re-running the full battery after every keystroke | Slow; thrashes a circular model | Use the tiers - in-flight discipline, step-gate, full run |
| Editing a cell then moving it into position | Creates stray links and displaced content | Edit in place (Tier 1 discipline) |
| Pasting a value over a formula to clear a check | Hides the error and kills the link | Fix the formula, never the symptom |
| Failing a legitimate sourced input as a "hardcode" | Over-strict - a sourced input is valid | Only orphan/unsourced hardcodes fail (rule: Data & Formula skills) |
| Blocking on an intended aggressive assumption | Sanity checks are judgement, not law | Warn, don't block; let the human decide |
| Passing a model that balances by a plug | The balance row reads 0 but is forced | Check for a plug cell, not just the result |
| Restating another skill's specific checks in QA | QA over-reaching; duplication drifts | Keep specific checks in their skill; QA owns the universal layer only |`;
